import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  UserPlus,
  Settings,
  Download,
  Target,
  Calendar,
  Mail,
  FileText,
  Users
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Action {
  title: string;
  description: string;
  icon: LucideIcon;
  href: string;
  variant: 'default' | 'outline';
  onClick?: () => void;
}

interface QuickActionsProps {
  isAdmin: boolean;
}

export function QuickActions({ isAdmin }: QuickActionsProps) {
  const adminActions: Action[] = [
    {
      title: 'Add New User',
      description: 'Invite players or coaches',
      icon: UserPlus,
      href: '/admin/users/new',
      variant: 'default'
    },
    {
      title: 'Organization Settings',
      description: 'Manage team settings',
      icon: Settings,
      href: '/admin/settings',
      variant: 'outline'
    },
    {
      title: 'Export Data',
      description: 'Download analytics report',
      icon: Download,
      href: '#',
      variant: 'outline',
      onClick: () => {
        // Handle export functionality
        console.log('Exporting data...');
      }
    },
    {
      title: 'Send Invites',
      description: 'Bulk invite team members',
      icon: Mail,
      href: '/admin/users/invite',
      variant: 'outline'
    }
  ];

  const coachActions: Action[] = [
    {
      title: 'Schedule Session',
      description: 'Book training session',
      icon: Calendar,
      href: '/admin/coach/schedule',
      variant: 'default'
    },
    {
      title: 'View Players',
      description: 'Manage your team',
      icon: Users,
      href: '/admin/coach/players',
      variant: 'outline'
    },
    {
      title: 'Create Drill',
      description: 'Design new practice',
      icon: Target,
      href: '/admin/coach/drills',
      variant: 'outline'
    },
    {
      title: 'Progress Report',
      description: 'Generate player reports',
      icon: FileText,
      href: '/admin/analytics/reports',
      variant: 'outline'
    }
  ];

  const actions: Action[] = isAdmin ? adminActions : coachActions;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick Actions</CardTitle>
        <CardDescription>
          {isAdmin ? 'Common administrative tasks' : 'Coaching tools and shortcuts'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {actions.map((action, index) => (
            <div key={index}>
              {action.href === '#' ? (
                <Button
                  variant={action.variant}
                  className="w-full justify-start"
                  onClick={action.onClick}
                >
                  <action.icon className="mr-2 h-4 w-4" />
                  <div className="text-left">
                    <div className="font-medium">{action.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {action.description}
                    </div>
                  </div>
                </Button>
              ) : (
                <Link to={action.href}>
                  <Button
                    variant={action.variant}
                    className="w-full justify-start"
                  >
                    <action.icon className="mr-2 h-4 w-4" />
                    <div className="text-left">
                      <div className="font-medium">{action.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {action.description}
                      </div>
                    </div>
                  </Button>
                </Link>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}